import Vue from "vue";
import DatePicker from "vue2-datepicker";
import VueSimpleSuggest from "vue-simple-suggest";
import SettingPopup from "@/components/dialogs/quotation-item";
import moment from "moment";

const emptyRow = () => ({
  sku_id: null,
  // ชนิดสินค้าจาก SKU (1=แผ่น 2=ครอบ ...) — ใช้คุมว่าติดฉนวนได้ไหม
  main_product_id: null,
  code: "",
  code_description: "",
  description: "",
  unit_name: "",
  length: 0, // ความยาว/เส้น
  quantity: 0,
  roll_multiplier: 1, // ตัวคูณลอน (จาก SKU ที่เลือก) — default 1
  total_length: 0, // = length × quantity × roll_multiplier (คำนวณอัตโนมัติ)
  remark: "",
  // option (จาก gear popup) — match กับ PownganOrderItem
  is_insulator: false,
  isPE: true,
  isPU: false,
  thickness: null,
  insulator_material: null,
  insulator_detail: "",
  insulator_head: "",
  insulator_tail: "",
  is_curve: false,
  curve_weight: "",
  curve_start: "",
  curve_gap: "",
  curve_repeat: "",
  _files: [], // frontend-only — เก็บ File objects ก่อน upload
});

export default {
  components: { DatePicker, VueSimpleSuggest, SettingPopup },

  data() {
    return {
      role: "",
      branch: "",
      branches: [],

      skuOptions: [],
      skuLoaded: false,

      // gear popup state
      isSettingOpened: false,
      settingData: {},
      settingIndex: -1,

      form: {
        producer_branch_id: null,
        document_date: new Date(),
        remark: "",
        products: [],
      },

      submitting: false,
    };
  },

  computed: {
    producerBranchOptions() {
      const myBranch = Number(this.branch);
      return (this.branches || []).filter((b) => Number(b.id) !== myBranch);
    },
  },

  methods: {
    // หน่วยของรายการ (เมตร/กิโล/ตร.ม. ฯลฯ) — หัวตารางไม่ฟิกซ์ (ม.) แล้ว
    // เพราะโป๊วงานรับได้หลายหน่วย ต้องโชว์หน่วยจริงต่อแถวให้ตรงกับที่กรอก
    unitLabel(it) {
      const u = (it && (it.unit_name || it.unit)) || ''
      return String(u).trim() || 'ม.'
    },
    addRow() {
      this.form.products.push(emptyRow());
    },

    // ความยาวรวมของแถว = ความยาว/เส้น × จำนวน (ปริมาณผลิตจริง)
    // ⚠️ ไม่คูณตัวคูณลอน — ตัวคูณลอนแปลกใช้ตอนคิดเงินเท่านั้น (backend คูณตอน pricing)
    rowTotalLength(row) {
      const len = Number(row?.length) || 0;
      const qty = Number(row?.quantity) || 0;
      return Math.round(len * qty * 100) / 100;
    },

    // สรุป option ที่ตั้งจาก popup (เฟือง) มาแสดงใต้ชื่อ SKU
    // thickness/insulator_material เก็บเป็น uuid → map เป็นชื่อผ่าน global helper mixin
    optionSummary(row) {
      if (!row) return "";
      const parts = [];

      if (row.is_insulator) {
        let t = "ติดฉนวน";
        if (row.isPU) {
          t += " PU";
          const o = (this.thicknessOptions2 || []).find((x) => x.uuid == row.thickness);
          if (o) t += " " + o.name;
        } else if (row.isPE) {
          t += " PE";
          const o = (this.thicknessOptions || []).find((x) => x.uuid == row.thickness);
          if (o) t += " " + o.name;
        }
        const mat = (this.insulatorMaterialOptions || []).find((x) => x.uuid == row.insulator_material);
        if (mat) t += " ท้อง" + mat.name;
        if (row.insulator_detail) t += " " + row.insulator_detail;
        if (row.insulator_head) t += " / เว้นหัว " + row.insulator_head;
        if (row.insulator_tail) t += " เว้นท้าย " + row.insulator_tail;
        parts.push(t);
      }

      if (row.is_curve) {
        let c = "ย้ำโค้ง";
        if (row.curve_weight) c += " " + row.curve_weight;
        if (row.curve_start) c += " เริ่ม " + row.curve_start;
        if (row.curve_gap) c += " ห่าง " + row.curve_gap;
        if (row.curve_repeat) c += " ย้ำ " + row.curve_repeat;
        parts.push(c);
      }

      return parts.join(" · ");
    },

    // แยกบรรทัด: ติดฉนวน / ย้ำโค้ง อ่านง่ายกว่าต่อกันด้วย · บรรทัดเดียว
    optionSummaryLines(row) {
      const t = this.optionSummary(row);
      return t ? String(t).split(" · ").filter((x) => x && x.trim()) : [];
    },

    // คัดลอกรายการ — ได้ทุกอย่างรวม option/ตัวคูณลอน แต่ไม่เอาไฟล์แนบ
    // (_files ต้องเป็น array ใหม่ ไม่งั้นสองแถวแชร์ reference กัน ลบรูปแถวหนึ่งแล้วอีกแถวหายด้วย)
    copyRow(idx) {
      const src = this.form.products[idx];
      if (!src) return;
      const { _files, ...rest } = src;
      this.form.products.splice(idx + 1, 0, { ...rest, _files: [] });
    },

    removeRow(idx) {
      this.form.products.splice(idx, 1);
    },

    // ติดฉนวนได้เฉพาะแผ่น (1) / ครอบ (2) — เปลี่ยน SKU เป็นอย่างอื่นต้องล้างค่าฉนวนทิ้ง
    // ไม่งั้นสกรูจะพก is_insulator ติดไปด้วย (เจอจริงในข้อมูลเก่า)
    clearInsulatorIfNotAllowed(row) {
      if (!row) return;
      const t = Number(row.main_product_id);
      if ([1, 2].indexOf(t) !== -1) return;
      row.is_insulator = false;
      row.isPE = true;
      row.isPU = false;
      row.thickness = null;
      row.insulator_material = null;
      row.insulator_detail = "";
      row.insulator_head = "";
      row.insulator_tail = "";
    },

    openSetting(idx) {
      this.settingIndex = idx;
      this.settingData = this.form.products[idx];
      this.isSettingOpened = true;
    },

    onSettingClose(e) {
      this.isSettingOpened = false;
      if (e && e.status && typeof e.index === "number" && this.form.products[e.index]) {
        // popup เขียนค่ากลับเข้ามาที่ object เดิม (reference) อยู่แล้ว — ไม่ต้องทำอะไรเพิ่ม
      }
    },

    onPickRowFiles(idx, event) {
      const files = Array.from(event?.target?.files || []);
      const row = this.form.products[idx];
      if (!row) return;
      for (const f of files) {
        if (!f.type.startsWith("image/")) {
          this.swalError(`ไฟล์ ${f.name} ไม่ใช่รูปภาพ`);
          continue;
        }
        if (f.size > 10 * 1024 * 1024) {
          this.swalError(`ไฟล์ ${f.name} ใหญ่เกิน 10MB`);
          continue;
        }
        f._preview = URL.createObjectURL(f);
        row._files.push(f);
      }
      event.target.value = "";
    },

    removeRowFile(idx, fileIdx) {
      const row = this.form.products[idx];
      if (!row) return;
      const file = row._files[fileIdx];
      if (file && file._preview) URL.revokeObjectURL(file._preview);
      row._files.splice(fileIdx, 1);
    },

    skuFilter(item, query) {
      if (!query) return true;
      const q = String(query).toLowerCase();
      return (
        String(item.full_code || "").toLowerCase().includes(q) ||
        String(item.name || "").toLowerCase().includes(q)
      );
    },

    checkTag(suggestion, index) {
      // user คลิกเลือกจาก suggestion — suggestion คือ object SKU
      // (library ตั้ง v-model = full_code ให้เองผ่าน autocompleteText)
      if (!suggestion) return;
      const row = this.form.products[index];
      if (!row) return;
      row.sku_id = suggestion.id;
      row.code_description = suggestion.name;
      row.main_product_id = suggestion.main_product_id ?? null;
      // เก็บตัวคูณลอนจาก SKU (ลอนปกติ = 1) → ใช้คูณปริมาณรวม
      row.roll_multiplier = Number(suggestion.roll_multiplier) || 1;
      // เปลี่ยนไปเป็นสินค้าที่ติดฉนวนไม่ได้ ต้องล้างค่าฉนวนที่ติ๊กไว้ก่อนหน้า
      this.clearInsulatorIfNotAllowed(row);
      // บังคับปิด dropdown — library re-focus หลังคลิก เลยเปิดค้าง
      setTimeout(() => {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      }, 10);
    },

    checkSelect(e, index) {
      // fallback: user พิมพ์เอง — ลอง match แบบ exact ทั้ง code และ name
      if (!this.skuLoaded) return;
      const q =
        typeof e === "string"
          ? e
          : e?.full_code || e?.target?.value || "";
      const row = this.form.products[index];
      if (!row) return;
      const lc = String(q).toLowerCase();
      const i = this.skuOptions.findIndex(
        (p) =>
          String(p.full_code || "").toLowerCase() === lc ||
          String(p.name || "").toLowerCase() === lc
      );
      if (i !== -1) {
        row.sku_id = this.skuOptions[i].id;
        row.code_description = this.skuOptions[i].name;
        row.main_product_id = this.skuOptions[i].main_product_id ?? null;
        row.roll_multiplier = Number(this.skuOptions[i].roll_multiplier) || 1;
      } else {
        row.sku_id = null;
        row.code_description = "";
        row.main_product_id = null;
        row.roll_multiplier = 1;
      }
      this.clearInsulatorIfNotAllowed(row);
    },

    async loadSkuOptions() {
      this.skuOptions = [];
      try {
        const result = await Vue.prototype.$axios.get(`${Vue.prototype.$host}/api/v1/master/sku`);
        this.skuOptions = result?.data || [];
        this.skuLoaded = true;
      } catch (e) {
        this.skuOptions = [];
      }
    },

    async getRole() {
      try {
        const res = await this.$store.dispatch("Auth/getRole", {}, { root: true });
        this.role = res?.data?.role || "";
        this.branch = res?.data?.branch_id || "";
      } catch (e) {
        this.role = "";
        this.branch = "";
      }
    },

    async loadBranches() {
      try {
        const res = await Vue.prototype.$axios.get(`${Vue.prototype.$host}/api/v1/branches`);
        const data = res?.data?.data || res?.data || [];
        this.branches = Array.isArray(data) ? data : (data?.data || []);
      } catch (e) {
        this.branches = [];
      }
    },

    async onSubmit() {
      if (!this.form.producer_branch_id) {
        return this.swalError("กรุณาเลือกสาขาผู้รับผลิต");
      }
      if (!this.form.products.length) {
        return this.swalError("กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการ");
      }

      const skuIdSet = new Set(this.skuOptions.map((s) => s.id));
      const invalidRows = [];
      const zeroQtyRows = [];
      const zeroLengthRows = [];
      this.form.products.forEach((it, idx) => {
        if (!it.sku_id || !skuIdSet.has(it.sku_id)) {
          invalidRows.push(idx + 1);
        }
        if (!it.quantity || Number(it.quantity) <= 0) {
          zeroQtyRows.push(idx + 1);
        }
        // ความยาวเป็นฟิลด์บังคับ (มี * แดงที่หัวคอลัมน์) — ใช้คิดปริมาณรวม/ราคา
        if (!it.length || Number(it.length) <= 0) {
          zeroLengthRows.push(idx + 1);
        }
      });
      if (invalidRows.length > 0) {
        return this.swalError(
          `รายการลำดับที่ ${invalidRows.join(", ")} ยังไม่ได้เลือก SKU หรือ SKU ไม่ถูกต้อง`
        );
      }
      if (zeroQtyRows.length > 0) {
        return this.swalError(
          `รายการลำดับที่ ${zeroQtyRows.join(", ")} ต้องระบุจำนวนมากกว่า 0`
        );
      }
      if (zeroLengthRows.length > 0) {
        return this.swalError(
          `รายการลำดับที่ ${zeroLengthRows.join(", ")} ต้องระบุความยาวมากกว่า 0`
        );
      }

      this.submitting = true;
      try {
        // ตัด _files ออกก่อนส่ง — backend ไม่ต้องการ
        // total_length คำนวณจาก length × quantity ตรงนี้ (source of truth ก่อนส่ง)
        const cleanProducts = this.form.products.map((p) => {
          const { _files, ...rest } = p;
          rest.total_length = this.rowTotalLength(p);
          return rest;
        });

        const payload = {
          producer_branch_id: this.form.producer_branch_id,
          document_date: this.form.document_date
            ? moment(this.form.document_date).format("YYYY-MM-DD HH:mm:ss")
            : null,
          remark: this.form.remark,
          products: JSON.stringify(cleanProducts),
        };

        const res = await Vue.prototype.$axios.post(
          `${Vue.prototype.$host}/api/v1/document/powngan`,
          payload
        );

        const uuid = res?.data?.uuid;
        const createdItems = res?.data?.items || [];

        // อัพไฟล์ของแต่ละแถว — pair ด้วย index (backend คืน items เรียงตาม id asc = ตามลำดับ insert)
        let uploadFailed = 0;
        for (let i = 0; i < this.form.products.length; i++) {
          const itemId = createdItems[i]?.id;
          const files = this.form.products[i]._files || [];
          if (!itemId || !files.length) continue;
          for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
              await Vue.prototype.$axios.post(
                `${Vue.prototype.$host}/api/v1/document/powngan/items/${itemId}/attachments`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } }
              );
            } catch (e) {
              uploadFailed++;
            }
          }
        }

        this.$swal({
          icon: "success",
          title: "สร้างใบสั่งโป๊วสำเร็จ",
          text: uploadFailed
            ? `เลขที่: ${res?.data?.document_no || ""} (มี ${uploadFailed} ไฟล์อัพไม่ผ่าน)`
            : `เลขที่: ${res?.data?.document_no || ""}`,
          confirmButtonText: "ตกลง",
        }).then(() => {
          if (uuid) this.$router.push(`/powngan/${uuid}`);
          else this.$router.push("/powngan");
        });
      } catch (error) {
        this.swalError(error?.response?.data?.message || error?.message || "บันทึกไม่สำเร็จ");
      } finally {
        this.submitting = false;
      }
    },

    swalError(msg) {
      this.$swal({ icon: "error", title: "ผิดพลาด", text: msg, confirmButtonText: "ตกลง" });
    },
  },

  async mounted() {
    // โหลดขนาน — เดิม await ต่อคิว ทำให้ dropdown สาขาต้องรอ /me เสร็จก่อน
    // (ทั้ง 3 ตัวไม่มี dependency ต่อกัน; ส่วนที่ใช้ role/branch เป็น reactive อยู่แล้ว)
    await Promise.all([
      this.getRole(),
      this.loadBranches(),
      this.loadSkuOptions(),
    ]);
  },
};
